import { useState, useRef } from 'react';
import { PLANBASE_CONFIG } from '../config';
import { getUploadHeaders, decodeJWT } from '../utils/auth';
import imageCompression from 'browser-image-compression';

import { ArrowPathIcon, PhotoIcon } from '@heroicons/react/24/outline';

interface ImageUploadZoneProps {
  onImageUploaded: (url: string) => void;
  maxFiles?: number;
  currentCount?: number;
  label?: string;
  required?: boolean;
}

export function ImageUploadZone({ 
  onImageUploaded, 
  maxFiles = 1, 
  currentCount = 0,
  label = 'Upload Image',
  required = false 
}: ImageUploadZoneProps) {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadImage = async (file: File) => {
    if (currentCount >= maxFiles) {
      setError(`Maximum ${maxFiles} image${maxFiles > 1 ? 's' : ''} allowed`);
      return;
    }

    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    // Allow up to 20MB before compression — we'll compress it down
    if (file.size > 20 * 1024 * 1024) {
      setError('Image size must be less than 20MB');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const token = localStorage.getItem('planbase_token');
      if (!token) throw new Error('Please log in to upload images');

      const payload = decodeJWT(token);
      const userId = payload?.sub || payload?.user_id || payload?.id;

      // ── Compress before upload ────────────────────────────────────────
      let fileToUpload = file;
      const originalKB = Math.round(file.size / 1024);

      if (file.size > 800 * 1024) {
        setProgress('Compressing...');
        fileToUpload = await imageCompression(file, {
          maxSizeMB: 1,           // target ≤1MB
          maxWidthOrHeight: 2048, // cap resolution
          useWebWorker: true,
          onProgress: (pct) => setProgress(`Compressing ${pct}%`),
        });
        const compressedKB = Math.round(fileToUpload.size / 1024);
        console.log(`[ImageUpload] Compressed ${originalKB}KB → ${compressedKB}KB`);
      }

      setProgress('Uploading...');

      const formData = new FormData();
      formData.append('file', fileToUpload);
      formData.append('folder', `projects/${userId}`);

      const response = await fetch(`${PLANBASE_CONFIG.apiUrl}/sdk/storage/upload`, {
        method: 'POST',
        headers: {
          ...getUploadHeaders(),
          'X-User-ID': userId,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error?.message || errorData.error || errorData.message || 'Failed to upload image');
      }

      const data = await response.json();
      if (!data.url) throw new Error('No URL returned from upload');

      onImageUploaded(data.url);
    } catch (err: any) {
      setError(err.message || 'Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
      setProgress('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      uploadImage(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      uploadImage(e.target.files[0]);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div>
      <label className="block text-sm font-medium text-foreground mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      
      <div
        onClick={handleClick}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        className={`relative border border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          dragActive
            ? 'border-orange-400 bg-orange-50 dark:bg-orange-950/20'
            : uploading
            ? 'border-border bg-muted'
            : 'border-muted-foreground/30 hover:border-orange-400 hover:bg-muted/50'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
          disabled={uploading || currentCount >= maxFiles}
        />

        {uploading ? (
          <div className="flex flex-col items-center">
            <ArrowPathIcon className="h-8 w-8 text-primary animate-spin mb-2" />
            <p className="text-sm text-muted-foreground">{progress || 'Processing...'}</p>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 rounded-full bg-orange-100 dark:bg-orange-950 flex items-center justify-center mb-3">
              <PhotoIcon className="h-8 w-8 text-primary" />
            </div>
            <p className="text-sm font-medium text-foreground mb-1">
              Click to upload or drag and drop
            </p>
            <p className="text-xs text-muted-foreground">
              PNG, JPG, GIF up to 20MB · Auto-compressed
            </p>
            {maxFiles > 1 && (
              <p className="text-xs text-muted-foreground mt-2">
                {currentCount}/{maxFiles} images
              </p>
            )}
          </div>
        )}
      </div>

      {error && (
        <p className="mt-2 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
}
